'use client';

import React, { useEffect, useMemo } from 'react';
import { useVideoModal } from '@/contexts/VideoModalContext';

export default function VideoModal() {
  const { isOpen, videoUrl, videoAlt, objectPosition, closeModal } = useVideoModal();
  
  // Determine if the URL is a video or image
  const isVideo = useMemo(() => {
    if (!videoUrl) return false;
    const url = videoUrl.toLowerCase();
    return url.includes('.mp4') || url.includes('.webm') || url.includes('.mov') || url.includes('video');
  }, [videoUrl]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeModal();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closeModal]);

  // Handle iOS fullscreen exit
  useEffect(() => {
    if (!isOpen || !videoUrl) return;

    const handleFullscreenChange = () => {
      const isFullscreen = document.fullscreenElement || 
        (document as Document & { webkitFullscreenElement?: Element }).webkitFullscreenElement ||
        (document as Document & { mozFullScreenElement?: Element }).mozFullScreenElement ||
        (document as Document & { msFullscreenElement?: Element }).msFullscreenElement;
      
      if (!isFullscreen) {
        setTimeout(() => {
          closeModal();
        }, 100);
      }
    };

    const handleiOSFullscreenExit = () => {
      closeModal();
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    const videoElement = document.querySelector('video[autoplay]');
    if (videoElement) {
      videoElement.addEventListener('webkitendfullscreen', handleiOSFullscreenExit);
      const handlePause = () => {
        const isFullscreen = document.fullscreenElement || 
          (document as Document & { webkitFullscreenElement?: Element }).webkitFullscreenElement;
        if (!isFullscreen) {
          setTimeout(() => {
            const video = videoElement as HTMLVideoElement;
            if (video.paused && video.currentTime > 0) {
              closeModal();
            }
          }, 200);
        }
      };
      videoElement.addEventListener('pause', handlePause);
      
      return () => {
        document.removeEventListener('fullscreenchange', handleFullscreenChange);
        document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
        document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
        videoElement.removeEventListener('webkitendfullscreen', handleiOSFullscreenExit);
        videoElement.removeEventListener('pause', handlePause);
      };
    }

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, [isOpen, videoUrl, closeModal]);

  if (!isOpen || !videoUrl) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 flex flex-col items-center justify-center bg-black/90 px-4"
      style={{
        zIndex: 99999,
      }}
      onClick={closeModal}
    >
      {/* Tap outside indicator - top */}
      <div className="absolute top-6 left-0 right-0 flex justify-center pointer-events-none">
        <span className="text-white/50 text-sm font-medium animate-pulse">
          Tap outside to close
        </span>
      </div>
      
      <div
        className="relative w-full max-w-5xl max-h-[90vh]"
        style={{ zIndex: 100000 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button - always visible, high z-index */}
        <button
          type="button"
          className="absolute -top-12 right-0 md:top-4 md:right-4 rounded-full bg-white text-black hover:bg-gray-200 px-4 py-2 text-base font-semibold transition-all cursor-pointer shadow-lg"
          style={{ 
            zIndex: 100001,
            touchAction: 'manipulation',
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            closeModal();
          }}
          onTouchEnd={(e) => {
            e.preventDefault();
            e.stopPropagation();
            closeModal();
          }}
        >
          ✕ Close
        </button>
        {isVideo ? (
          <video
            src={videoUrl}
            controls
            autoPlay
            playsInline
            className="w-full max-h-[90vh] rounded-lg bg-black"
            style={{ 
              zIndex: 100000,
              objectFit: 'contain',
              objectPosition: objectPosition || 'top'
            }}
            onClick={(e) => e.stopPropagation()}
            onEnded={closeModal}
          >
            Tu navegador no soporta el tag de video.
          </video>
        ) : (
          <img
            src={videoUrl}
            alt={videoAlt || 'Preview'}
            className="w-full max-h-[90vh] object-contain rounded-lg"
            style={{ objectPosition: objectPosition || 'top' }}
            onClick={(e) => e.stopPropagation()}
          />
        )}
      </div>
      
      {/* Tap outside indicator - bottom */}
      <div className="absolute bottom-6 left-0 right-0 flex justify-center pointer-events-none">
        <span className="text-white/40 text-xs">
          ↑ Tap anywhere outside the video ↑
        </span>
      </div>
    </div>
  );
}

